import {useCallback, useMemo} from 'react';
// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Listbox} from '@headlessui/react';
import {IconCaretFilled, IconCheck} from '~/components';
import clsx from 'clsx';
import {twMerge} from 'tailwind-merge';
import {DATA_TEST_IDS} from '~/lib/const';

const tshirtSizeOrder = [
  'XS',
  'XS-S',
  'XS/S',
  'XS S',
  'S',
  'S-M',
  'S/M',
  'S M',
  'M',
  'M-L',
  'M/L',
  'M L',
  'L',
  'L-XL',
  'L/XL',
  'L XL',
  'XL',
  'XL-2XL',
  'XL/2XL',
  'XL 2XL',
  '2XL',
  '2XL-3XL',
  '2XL/3XL',
  '2XL 3XL',
  '3XL',
  '3XL-4XL',
  '3XL/4XL',
  '3XL 4XL',
  '4XL',
  'XL-XXL',
  'XL/XXL',
  'XL XXL',
  'XXL',
  'XXL-XXXL',
  'XXL/XXXL',
  'XXL XXXL',
  'XXXL',
  'XXXL-XXXXL',
  'XXXL/XXXXL',
  'XXXL XXXXL',
  'XXXXL',
  'XXXXL-XXXXXL',
  'XXXXL/XXXXXL',
  'XXXXL XXXXXL',
  'XXXXXL',
];

const romanNumeralOrder = [
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
];

export function OptionsDropdown({
  name,
  classNameButton,
  selectedValue,
  values,
  availableValues,
  error,
  sortDisabled,
  onChange,
}: {
  name: string;
  classNameButton?: string;
  selectedValue?: string;
  values: string[];
  availableValues?: string[];
  error?: boolean;
  sortDisabled?: boolean;
  onChange: (name: string, value: string) => void;
}) {
  const handleChange = useCallback(
    (value: string) => {
      onChange(name, value);
    },
    [name, onChange],
  );

  const orderedValues = useMemo(() => {
    if (sortDisabled) return values;

    return values.sort((a, b) => {
      const isACurrency = a.startsWith('$');
      const isBCurrency = b.startsWith('$');

      if (isACurrency && isBCurrency) {
        return parseFloat(a.slice(1).trim()) - parseFloat(b.slice(1).trim());
      }

      const isANumber = !isNaN(parseFloat(a));
      const isBNumber = !isNaN(parseFloat(b));

      if (isANumber && isBNumber) {
        return parseFloat(a) - parseFloat(b);
      }

      const aTShirtOrder = tshirtSizeOrder.indexOf(a.toUpperCase());
      const bTShirtOrder = tshirtSizeOrder.indexOf(b.toUpperCase());

      if (aTShirtOrder !== -1 && bTShirtOrder !== -1) {
        return aTShirtOrder - bTShirtOrder;
      }

      const aRomanNumeralOrder = romanNumeralOrder.indexOf(a.toUpperCase());
      const bRomanNumeralOrder = romanNumeralOrder.indexOf(b.toUpperCase());

      if (aRomanNumeralOrder !== -1 && bRomanNumeralOrder !== -1) {
        return aRomanNumeralOrder - bRomanNumeralOrder;
      }

      return a.localeCompare(b);
    });
  }, [values, sortDisabled]);

  return (
    <Listbox onChange={handleChange} value="">
      {({open}) => {
        return (
          <>
            <Listbox.Button
              data-testid={DATA_TEST_IDS.LISTBOX_BUTTON}
              className={twMerge(
                clsx(
                  `flex items-center justify-between w-full md:max-w-[224px] py-3.5 px-3 border-2 ${
                    open
                      ? 'rounded-b md:rounded-t md:rounded-b-none'
                      : 'rounded'
                  }`,
                  classNameButton,
                  error ? 'border-red-500' : 'border-gray-100',
                ),
              )}
            >
              <span className="first-letter:capitalize">
                {selectedValue
                  ? decodeURIComponent(selectedValue).toUpperCase()
                  : ''}
                &nbsp;
              </span>
              <IconCaretFilled
                className={'w-[12px] h-[7px]'}
                direction={open ? 'up' : 'down'}
              />
            </Listbox.Button>

            <Listbox.Options
              className={`bg-contrast absolute z-30 flex flex-col
                w-full md:max-w-[224px] overflow-y-scroll rounded-t border-2 border-gray-100 px-2 py-2 transition-[max-height]
                duration-150 sm:bottom-auto md:rounded-b md:rounded-t-none md:border-t-0 md:border-b ${
                  open ? 'max-h-48' : 'max-h-0'
                }`}
            >
              {orderedValues.map((value) => {
                const isSelected =
                  selectedValue === value ||
                  (selectedValue &&
                    decodeURIComponent(selectedValue).toLowerCase() ===
                      value.toLowerCase());
                const id = `option-${name}-${value}`;

                return (
                  <Listbox.Option
                    key={id}
                    value={value}
                    data-testid={DATA_TEST_IDS.LISTBOX_OPTION}
                  >
                    {({active}) => (
                      <div
                        className={`text-primary w-full font-header p-2 transition rounded flex justify-start items-center text-left cursor-pointer ${
                          active ? 'bg-primary/10' : null
                        }`}
                      >
                        {Array.isArray(availableValues)
                          ? availableValues.includes(value)
                            ? value.toUpperCase()
                            : `${value.toUpperCase()} (sold out)`
                          : value.toUpperCase()}
                        {isSelected ? (
                          <span
                            data-testid={DATA_TEST_IDS.CHECKBOX('checked')}
                            className="ml-2"
                          >
                            <IconCheck />
                          </span>
                        ) : null}
                      </div>
                    )}
                  </Listbox.Option>
                );
              })}
            </Listbox.Options>
          </>
        );
      }}
    </Listbox>
  );
}
